import { autoinject, BindingEngine, transient, computedFrom, useView } from "aurelia-framework";
import { I18N } from "aurelia-i18n";
import {
    CargoTypeDistributionModel,
    GetVoyageDataSubmissionComputationErrorsResponse,
    GroupedVoyageValidationErrorModel, LastFourSubmissionsSummaryModel,
    VoyageDataSubmissionClientModel
} from "../../../../../../models/vetting/vetting.models";
import { VoyageDataSubmissionService } from "../../../../../../services/vetting/voyage-data-submission.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import {
    DataGridColumn,
    DataType, EntityMetaDataModel,
    ItemSource,
    QueryFilter,
    SourceType
} from "../../../../../../models/queries/queries.models";
import { CustomGridPageBase } from "../../../../common/custom-grid-page.base";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { SignalFunctions } from "../../../../../../services/basics/signal-functions";
import { Subscription } from "aurelia-event-aggregator";
import { NumberFormatHelper } from "../../../../../../helpers/number-format-helper";
import * as moment from "moment";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../../constants/authentication/claims";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import {QueriesClientService} from "../../../../../../services/queries/queries-client.services";
import * as Enumerable from "linq";

@autoinject
@transient()
@useView('views/pages/modules/voyage-data-submission/steps/voyage-records/voyage-data-submission-voyage-records.html')
export class VoyageDataSubmissionVoyageRecords extends CustomGridPageBase {

    public item: VoyageDataSubmissionClientModel;
    
    public validationErrors: GroupedVoyageValidationErrorModel[] = [];

    public computationErrorsResponse: GetVoyageDataSubmissionComputationErrorsResponse;

    public isClosed: boolean = true;

    public isComputationErrorsClosed: boolean = true;

    public isRightShipUser: boolean = true;

    public validationErrorsElement: HTMLElement;

    public computationErrorsElement: HTMLElement;

    public submissionsSummary: LastFourSubmissionsSummaryModel[] = [];
    
    public cargoDistribution: CargoTypeDistributionModel[] = [];

    public isResolvingErrors: boolean = false;

    constructor(
        protected i18N: I18N, private voyageDataSubmissionService: VoyageDataSubmissionService,
        private dialogService: DialogClientService, protected bindingEngine: BindingEngine, private signalService: SignalClientService,
        private userClaimsService: UserClaimsService, private notificationService: NotificationClientService,
        private queriesClientService: QueriesClientService
    ) {
        super(i18N, "Voyage Records", bindingEngine)

        this.isRightShipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
    }

    @computedFrom("item", "item.lastComputedDate")
    public get lastComputedDate()
    {
        if (!this.item || !this.item.lastComputedDate)
            return null;
        
        const result = moment(this.item.lastComputedDate).format('DD MMM YYYY HH:mm:ss');
        return result;
    }
    
    activate(model: any) {
        this.item = typeof (model) == "string" ? this.parse(<string>model) : model;
        this.getVoyageValidationErrors();
        this.getVoyageComputationErrors();
        this.getItemSource();
        this.voyageDataSubmissionService.getLastFourSubmissionsData(model.customerId, model.voyageDataSubmissionId)
            .then(response =>{
                    this.submissionsSummary = response.lastFourSubmissionsSummary
            });
        this.voyageDataSubmissionService.getCargoTypeDistribution(model.customerId, model.voyageDataSubmissionId)
            .then(response =>{
                this.cargoDistribution = response.cargoTypeDistributions; 
        });
    }

    attached()
    {
        this.signalService.subscribe(SignalFunctions.voyageLegDataAttachmentParsedChanged, this.refreshData);
        this.signalService.subscribe(SignalFunctions.voyageEmissionsComputed, this.refreshData);
        this.signalService.subscribe(SignalFunctions.voyageResolveErrorStatusChanged, this.resolveErrorsChanged);
    }

    detached()
    {
        this.signalService.unsubscribe(SignalFunctions.voyageLegDataAttachmentParsedChanged, this.refreshData);
        this.signalService.unsubscribe(SignalFunctions.voyageEmissionsComputed, this.refreshData);
        this.signalService.unsubscribe(SignalFunctions.voyageResolveErrorStatusChanged, this.resolveErrorsChanged);
        this.disposeSubspcription();
        super.detached();
    }

    private resolveErrorsChanged = (data) => {
        if(data && data.voyageDataSubmissionId == this.item.voyageDataSubmissionId)
        {
            if(Object.keys(data).indexOf("successful") > -1 && this.isRightShipUser)
            {
                if(data.successful)
                {
                    this.notificationService.show("Errors resolved successfully", null, NotificationType.Success);
                }
                else
                {
                    this.notificationService.show("Failed to resolve errors", data.message, NotificationType.Error);
                }
            }
            this.isResolvingErrors = false;
            this.refresh();
        }
    };

    private refreshData = (data) => {
        if(data && data.id == this.item.voyageDataSubmissionId)
        {
            if(this.item && Object.keys(data).indexOf("lastComputedDate") > -1)
            {
                this.item.lastComputedDate = data.lastComputedDate;
                this.notificationService.show(this.i18N.tr('success-text'), "Emissions Computed Successfully", NotificationType.Success);
            }
            this.refresh();
        }
    };

    public refresh = () => {
        if(this.dataGridVM && this.item)
        {
            this.dataGridVM.reloadData();
        }        
        this.getVoyageValidationErrors();
        this.getVoyageComputationErrors();
    }
    
    public refreshVoyageSubmissionSummaryData = () => {
        this.voyageDataSubmissionService.getLastFourSubmissionsData(this.item.customerId, this.item.voyageDataSubmissionId)
            .then(response =>{
                this.submissionsSummary = response.lastFourSubmissionsSummary
            });
        this.voyageDataSubmissionService.getCargoTypeDistribution(this.item.customerId, this.item.voyageDataSubmissionId)
            .then(response =>{
                this.cargoDistribution = response.cargoTypeDistributions;
            });
    }

    private parse(obj: string): VoyageDataSubmissionClientModel {
        return JSON.parse(obj.replace(new RegExp("&quot;", "g"), "\"")
            .replace(new RegExp("&gt;", "g"), ">")
            .replace(new RegExp("&lt;", "g"), "<"));
    }

    getVoyageValidationErrors = () =>
    {
        this.voyageDataSubmissionService.getVoyageDataSubmissionErrors(this.item.voyageDataSubmissionId).then(res => {
            this.validationErrors = res;
        }, err => { console.error(err); });
    }

    getVoyageComputationErrors = () =>
    {
        this.voyageDataSubmissionService.getVoyageDataSubmissionComputationErrors(this.item.voyageDataSubmissionId).then(res => {
            this.computationErrorsResponse = res;
        }, err => { console.error(err); });
    }

    openResolveErrorsDialog()
    {
        this.dialogService.show(RightShipDialogServiceConstants.URLs.VoyageDataValidationErrorsDialog, {
            validationErrors: this.validationErrors,
            onSubmit: () => {
                this.isResolvingErrors = true;
                this.refresh()
            },
            customerId: this.item.customerId,
            voyageDataSubmissionId: this.item.voyageDataSubmissionId
        }, false, false, false, "right");
    }

    columnsSubscription: Subscription;

    private async getItemSource() {
        const initialFilters: QueryFilter[] = [];
        initialFilters.push(QueryFilter.getQuery("voyageDataSubmissionId", this.item.voyageDataSubmissionId, "eq", DataType.Number, true, false));

        let columnsEntityModel: EntityMetaDataModel
        await this.queriesClientService.getEntityModels().then(x => {
            columnsEntityModel = Enumerable.from(x).firstOrDefault(y => y.name === 'VoyageRecords');
        })

        for (const item of columnsEntityModel.projectionProperties) {
            const column: DataGridColumn = {
                columnOrder: 0,
                dataType: item.type,
                groupOrder: 0,
                isFilterable: true,
                isGrouping: false,
                isHidden: !item.showByDefault,
                isSortable: true,
                name: item.name,
                sortOrder: 0,
                title: item.friendlyName,
                isAscending: true
            };

            if (item.type === DataType.Date || item.type === DataType.DateTime && !column.valueConverter) {
                column.secondParameter = item.format;
            }

            this.columns.push(column);
        }

        this.itemSource = {
            sourceType: SourceType.ODataUrl,
            source: "odata/VoyageRecords",
            initialFilters: initialFilters,
            columns: this.columns
        };

        this.disposeSubspcription();

        this.columnsSubscription = this.bindingEngine.propertyObserver(this.itemSource, "columns").subscribe(
            () => {
                const itemSource: ItemSource = this.itemSource as ItemSource;
                if (itemSource.columns && itemSource.columns.length > 0) {
                    const converter = (value) => {
                        return (value ? NumberFormatHelper.getFormattedValue(value, null, 2, 0, null, 0) : 0) + '%';
                    }
                    const columnNames = <PercentageColumModel[]>[
                        {
                            columnName: "distanceCheck", markMismatching: true, getClass: (item, column) => {
                                return item && Object.keys(item).indexOf(column) > -1 && item[column] >= -10 && item[column] <= 20 ? 'valid' : 'invalid'
                            }
                        },
                        {
                            columnName: "fuelUseCheck", markMismatching: true, getClass: (item, column) => {
                                return item && Object.keys(item).indexOf(column) > -1 && item[column] <= 20 ? 'valid' : 'invalid'
                            }
                        },
                        {
                            columnName: "emissionsCheck", markMismatching: true, getClass: (item, column) => {
                                return item && Object.keys(item).indexOf(column) > -1 && item[column] <= 20 ? 'valid' : 'invalid'
                            }
                        },
                        {
                            columnName: "loadFactor", markMismatching: true, getClass: (item, column) => {
                                if (item && Object.keys(item).indexOf("isParcelled") > -1 && item["isParcelled"] == false) {
                                    return item && Object.keys(item).indexOf(column) > -1 && item[column] >= 70 && item[column] <= 100 ? 'valid' : 'invalid';
                                }
                                return null;
                            }
                        },
                        {columnName: "percentageOfParcel", markMismatching: false}
                    ]

                    const columns = itemSource.columns;

                    const percentColumns = columns.map((x, i) => {
                        return {column: x, index: i}
                    }).filter(x => columnNames.map(x => x.columnName).indexOf(x.column.name) > -1);
                    percentColumns.forEach(c => {
                        c.column.valueConverter = converter;
                        columns[c.index] = c.column;

                        const colMappings = columnNames.filter(x => x.columnName == c.column.name);
                        if (colMappings && colMappings.length > 0) {
                            const col = colMappings[0];
                            if (col.getClass)
                                c.column.getCellClass = (item) => {
                                    return col.getClass(item, col.columnName);
                                };
                        }
                    });

                    itemSource.columns = columns;
                }
            });
    }

    private disposeSubspcription()
    {
        if(this.columnsSubscription && this.columnsSubscription.dispose)
            this.columnsSubscription.dispose();
    }

    public toggle() {
        $(this.validationErrorsElement).slideToggle();
        this.isClosed = !this.isClosed;
    }

    public toggleComputationErrors() {
        $(this.computationErrorsElement).slideToggle();
        this.isComputationErrorsClosed = !this.isComputationErrorsClosed;
    }

    viewCargoTypesDialog() {
        this.dialogService.show(RightShipDialogServiceConstants.URLs.CargoTypesDialog, this.cargoDistribution, true, false, false);
    }
}

export class PercentageColumModel
{
    public columnName: string;
    public markMismatching: boolean = false;
    public getClass: (item, column) => string;
}
